import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment as env } from 'src/environments/environment';
import { RbacMatrix } from '../models/curso-detalle.model';

/**
 * Carga y expone la matriz RBAC de Vista 360 Curso (`{accion: [roles...]}`)
 * desde `GET /api/v1/curso/rbac-matrix`. Se hidrata una sola vez al arrancar
 * la app via APP_INITIALIZER (ver `app.config.ts`).
 *
 * Fallback silencioso: si el fetch falla (backend caído, red, etc.) la
 * matriz queda vacía ({}) y `can()` devuelve `false` para todo — la UI no
 * debe crashear al bootstrap por esto, simplemente se ocultan/deshabilitan
 * las acciones protegidas hasta que la matriz esté disponible.
 */
@Injectable({ providedIn: 'root' })
export class RbacService {
  private readonly http = inject(HttpClient);
  private readonly _matrix = signal<RbacMatrix>({});

  load(): Observable<void> {
    return this.http.get<RbacMatrix>(`${env.apiUrl}/api/v1/curso/rbac-matrix`).pipe(
      tap((m) => this._matrix.set(m)),
      map(() => undefined),
      catchError(() => {
        console.warn('[RbacService] load failed, using empty matrix');
        return of(undefined);
      })
    );
  }

  /** Case-insensitive: normaliza `role` a lowercase antes de buscar en la matriz. */
  can(role: string | null | undefined, action: string): boolean {
    if (!role) return false;
    return this._matrix()[action]?.includes(role.toLowerCase()) ?? false;
  }

  matrix(): RbacMatrix {
    return this._matrix();
  }
}
